import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Input, MessageBar, MessageBarBody, Text, tokens } from "@fluentui/react-components";
import { Sparkle20Regular } from "@fluentui/react-icons";
import { CELLS_PER_DAY, DAYS, slotsToCells, type Cells, type Weekday, type Window } from "@gymplan/shared";
import { api } from "../lib/api.ts";
import { AvailabilityGrid, type CellMark } from "./AvailabilityGrid.tsx";

type Interpretation =
  | { ok: true; slots: Window[]; changes: string[]; cellsChanged: number }
  | { ok: false; clarify: string; chips: string[] };

/**
 * "Change my hours" in plain words (#6) — the same parsing as setup. Always shows the result in words
 * and on the grid (green = added, red = removed) before anything is saved.
 */
export function ChangeHours({ current, putUrl, interpretUrl, onPreview }: { current: Cells; putUrl: string; interpretUrl: string; onPreview?: (open: boolean) => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [res, setResRaw] = useState<Interpretation | null>(null);
  const setRes = (r: Interpretation | null) => {
    setResRaw(r);
    onPreview?.(!!r?.ok);
  };
  const ask = useMutation({ mutationFn: (t: string) => api.post<Interpretation>(interpretUrl, { text: t }), onSuccess: setRes });
  const apply = useMutation({
    mutationFn: async () => {
      if (!res?.ok) return;
      if (res.cellsChanged > 0) await api.put(putUrl, { slots: res.slots.map((s) => ({ ...s, gymId: null })), via: "text" });
    },
    onSuccess: () => {
      setRes(null);
      setText("");
      void qc.invalidateQueries({ queryKey: ["availability"] });
      void qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });

  const preview = res?.ok ? slotsToCells(res.slots) : null;
  const marks = preview && res?.ok ? diffMarks(current, preview) : undefined;

  return (
    <Card style={{ padding: 16, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      <Text weight="semibold">Hours changed? Just say it</Text>
      <form style={{ display: "flex", gap: 8, flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); if (text.trim().length >= 2) ask.mutate(text.trim()); }}>
        <Input style={{ flex: 1, minWidth: 220 }} value={text} onChange={(_, d) => { setText(d.value); setRes(null); }} placeholder="e.g. add Sunday mornings · drop Friday evenings · Mondays 7–10 now" aria-label="Describe the change" />
        <Button type="submit" icon={<Sparkle20Regular />} disabled={text.trim().length < 2 || ask.isPending}>{ask.isPending ? "Reading…" : "Show me"}</Button>
      </form>
      {res && !res.ok && (
        <MessageBar intent="info">
          <MessageBarBody>
            {res.clarify}
            {res.chips.length > 0 && (
              <span style={{ display: "inline-flex", gap: 6, marginLeft: 8, flexWrap: "wrap" }}>
                {res.chips.map((c) => <Button key={c} size="small" shape="circular" onClick={() => { setText(c); ask.mutate(c); }}>{c}</Button>)}
              </span>
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      {res?.ok && preview && (
        <>
          <Text weight="semibold">Here's what would change:</Text>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {res.changes.map((c) => <li key={c}><Text>{c}</Text></li>)}
          </ul>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Green outline = added · red = removed. Nothing is saved until you confirm.</Text>
          <AvailabilityGrid value={preview} readOnly marks={marks} noQuickStart
            actions={
              <>
                <Button appearance="subtle" onClick={() => setRes(null)}>Cancel</Button>
                <Button appearance="primary" disabled={apply.isPending} onClick={() => apply.mutate()}>{apply.isPending ? "Saving…" : "Save this change"}</Button>
              </>
            }
          />
          {apply.isError && <MessageBar intent="error"><MessageBarBody>{(apply.error as Error).message}</MessageBarBody></MessageBar>}
        </>
      )}
    </Card>
  );
}

function diffMarks(before: Cells, after: Cells) {
  const m: Partial<Record<Weekday, (CellMark | null)[]>> = {};
  for (const d of DAYS) {
    const row = [...(m[d] ?? Array<CellMark | null>(CELLS_PER_DAY).fill(null))];
    for (let i = 0; i < CELLS_PER_DAY; i++) {
      if (after[d][i] && !before[d][i]) row[i] = "added";
      else if (!after[d][i] && before[d][i]) row[i] = "removed";
    }
    m[d] = row;
  }
  return m;
}
